import React, { FunctionComponent, useCallback, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { useLocation, useNavigate, useParams, UNSAFE_NavigationContext as NavigationContext  } from 'react-router-dom'
import View from '../../components/View/View'
import { useTranslation } from 'react-i18next'
import reservationStore from '../../stores/ReservationStore'
import modalStore from '../../stores/ModalStore'
import { EDIT, ReservationSteps } from '../../utils/constants'
import CargoInformation from './components/CargoInformation/CargoInformation'
import Confirmation from './components/Confirmation/Confirmation'
import DepartureComponent from './components/DepartureComponent/DepartureComponent'
import { DriverListFormValues } from './components/DriverAndServices/DriverAndServices'
import { calculateVehicleMaxLength } from '../../utils/bookingHelpers'
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Box,
  useToast,
  ListItem,
  OrderedList,
  useUpdateEffect
} from '@chakra-ui/react'
import { DoneIcon, EventIcon, AssignmentIcon, ErrorIcon } from '../../icons'
import { FormProvider, useForm } from 'react-hook-form'
import { DriversFormValues } from './components/Confirmation/SendLinkToDriver'
import { CargoNameListRow } from '../../api/cargoSelfServiceAPI'
import BaseFormLabel from '../../components/Form/BaseFormLabel'
import { getCargoInfoErrors } from '../../utils/reservationsHelpers'
import DriverServiceAccordion from '../../components/DriverServiceAccordion'
import userStore from '../../stores/UserStore'
import ErrorStore from '../../stores/ErrorStore'
import authStore from '../../stores/AuthStore'
import { Action } from '../../services/types'
import useQuery from '../../hooks/useQuery'
import { useNavigationBlocker } from '../../hooks/useNavigationBlocker'
import { VehicleCargoOptions } from './components/CargoInformation/VehicleCargoConfirmation'
import { format } from 'date-fns'
import css from './ReservationContainer.module.scss'

export type ReservationNativeFields = {
  vehicleRegistrationNumber: string
  vehicleRegistrationNumberCountryCode: string
  cargoType: string
  vehicleCargoConfirmation: string
  powerConnection: boolean
  vehicleTemplateCategoryExtCode: string
  width: number
  height: number
  loadWeight: number | string
  trailerRegistrationNumber: string
  trailerRegistrationNumberCountryCode: string
  externalNote: string
  internalNote: string
  vehicleLength: number
  consignee: string
}

export type ReservationFormValues = ReservationNativeFields &
  DriversFormValues &
  DriverListFormValues

export const driverDefaultValues: CargoNameListRow = {
  firstName: '',
  lastName: '',
  nationalityExtCode: '',
  dateOfBirth: '',
  mobile: '',
  genderCode: 'M',
  email: ''
}

export const cargoInfoFields: (keyof ReservationFormValues)[] = [
  'vehicleLength',
  'loadWeight',
  'vehicleRegistrationNumber',
  'cargoType',
  'vehicleTemplateCategoryExtCode',
  'vehicleCargoConfirmation',
  'externalNote',
  'internalNote',
  'consignee'
]

const defaultValues = (): Partial<ReservationFormValues> => ({
  width: 0,
  height: 0,
  email: '',
  phone: '',
  cargoType: '',
  vehicleCargoConfirmation: undefined,
  externalNote: '',
  internalNote: '',
  loadWeight: '',
  trailerRegistrationNumber: '',
  vehicleRegistrationNumber: '',
  vehicleTemplateCategoryExtCode: '',
  powerConnection: false,
  vehicleLength: 0,
  drivers: [driverDefaultValues]
})

const ReservationContainer: FunctionComponent = observer(() => {
  const { step = ReservationSteps['0'] } = useParams<{
    step: string
    bookingCode: string
    action: Action
  }>()
  const query = useQuery()

  const location = useLocation()

  useUpdateEffect(() => {
    // Reset the state when user navigates to New Reservation
    if (location.pathname === '/') {
      setIsDepartureValid(undefined)
      reservationStore.reset()
    }
  }, [location])

  const action = (query.get('action') as Action) || Action.New
  const bookingCode = query.get('bookingCode') || ''

  const navigate = useNavigate()
  const { t } = useTranslation()

  const [formReady, setFormReady] = useState(false)
  const [active, setActive] = useState<number>(0)
  const [isDepartureValid, setIsDepartureValid] = useState<boolean | undefined>(
    undefined
  )
  const [vehicleCargoConfirmationValue, setVehicleCargoConfirmationValue] = useState<
    string | undefined
  >(undefined)
  const toast = useToast()

  const methods = useForm<ReservationFormValues>({
    defaultValues: {
      ...defaultValues(),
      vehicleCargoConfirmation: vehicleCargoConfirmationValue
    },
    mode: 'onChange'
  })

  const fetchBooking = (bookingCode: string) => (
    setFormReady(false),
    reservationStore.fetchBooking(bookingCode).then((values) => {
      // Method to set initial values for change detection (used to track unsaved changes)
      reservationStore.setInitialReservationValues(values)
      methods.reset(values)
      // Form is ready after reset completes
      setFormReady(true)
    })
  )

  const updateStep = useCallback(() => {
    // Force the user to select the departure
    if (reservationStore.departureCode) {
      setActive(ReservationSteps[step])
    } else {
      setActive(0)
    }
  }, [reservationStore.departureCode, step])

  useEffect(() => {
    reservationStore.registerAutorun()

    return () => {
      reservationStore.disposeAutorun()
      methods.reset(defaultValues())
    }
  }, [])

  useEffect(() => {
    reservationStore.loadSavedDrivers()

    // Set the active step by default
    // Later on it is updated by the updateStep function
    setActive(ReservationSteps[step])

    reservationStore
      .fetchDeparturePorts()
      .then(reservationStore.fetchNationalities)
      .then(() => {
        if (bookingCode) {
          fetchBooking(bookingCode).then(updateStep)
        } else {
          reservationStore.reset()
          methods.reset(defaultValues())
        }
      })

    methods.trigger([
      'vehicleTemplateCategoryExtCode',
      'vehicleRegistrationNumber',
      'loadWeight',
      'cargoType',
      'vehicleCargoConfirmation'
    ])
  }, [bookingCode])

  useEffect(() => {
    // Update departure validity
    setIsDepartureValid((state) =>
      state !== undefined ? !!reservationStore.departureCode : undefined
    )
  }, [reservationStore.departureCode])

  // Hook for detecting unsaved changes in edit mode
  useEffect(() => {
    if (action !== Action.Edit) return

    if (!reservationStore.initialReservationValues) return
    if (!formReady) return

    // Skip the initial emission from watch to prevent race condition
    let skipNext = true
    const subscription = methods.watch((values) => {
      if (skipNext) {
        skipNext = false
        return
      }
      reservationStore.getHasUnsavedChanges(values as ReservationFormValues)
    })

    return () => subscription.unsubscribe()
  }, [action, reservationStore.initialReservationValues, formReady])

  // block logging out with unsaved changes
  useEffect(() => {
    if (action !== Action.Edit) return

    authStore.registerBeforeLogout(() =>
      new Promise((resolve) => {
        if (!reservationStore.hasUnsavedChanges) {
          resolve(true)
          return
        }
        modalStore.confirm(
          t('unsaved_changes_title'),
          t('unsaved_changes_description'),
          () => resolve(true)
        )
      })
    )

    return () => authStore.unregisterBeforeLogout()
  }, [action, reservationStore.hasUnsavedChanges])

  // block navigation with unsaved changes
  const handleBlockedNavigation = useCallback(
    ({ retry }: { retry: () => void }) => {
      modalStore.confirm(
        t('unsaved_changes_title'),
        t('unsaved_changes_description'),
        () => retry()
      )
    },
    [t]
  )

  useNavigationBlocker(
    handleBlockedNavigation,
    action === Action.Edit && reservationStore.hasUnsavedChanges
  )

  // beforeunload for browser tab close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (action === Action.Edit && reservationStore.hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = ''
      }
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [action, reservationStore.hasUnsavedChanges])

  const toggleActive = (id: number) => {
    if (reservationStore.departureCode) {
      setIsDepartureValid(true)
      setActive((prevValue) => {
        return prevValue === id ? -1 : id
      })
    } else {
      setIsDepartureValid(false)
      setActive(0)
    }
  }

  const onMakeReservation = methods.handleSubmit(
    async (values) => {
      ErrorStore.addLoader()

      if (action === Action.Edit) {
        let hasRemoteChanges = false
        try {
          hasRemoteChanges = await reservationStore.checkForRemoteChanges()
        } catch {
          // allow save if the check fails.
        }

        if (hasRemoteChanges) {
          toast({
            title: t('booking_remote_conflict_error'),
            status: 'error'
          })
          ErrorStore.removeLoader()
          return
        }
      }

      const isValid = await methods.trigger()

      await reservationStore.updateDepartures()
      const maxAllotment = calculateVehicleMaxLength({
        vehicleTemplate: reservationStore.findVehicleTemplateByCode(
          values.vehicleTemplateCategoryExtCode
        ),
        booking: reservationStore.booking,
        selectedDeparture: reservationStore.selectedDeparture,
        isOccasional: userStore.isOccasional
      })

      if (values.vehicleLength > maxAllotment) {
        toast({
          title: 'Error',
          status: 'error',
          description:
            'The total length of the vehicles exceeds the available allotment'
        })
        ErrorStore.removeLoader()
        return
      }

      if (!isValid) {
        ErrorStore.removeLoader()
        return
      }

      if (values.consignee) {
        values.consignee = values.consignee.toUpperCase()
      }

      values.drivers = values.drivers.map((driver) => ({
        ...driver,
        firstName: (driver.firstName || '').toUpperCase(),
        lastName: (driver.lastName || '').toUpperCase()
      }))

      const { email, phone } = values

      if (values.cargoType === 'VEHICLES') {
        const selectedConfirmationOption = Object.values(VehicleCargoOptions).find(
          (option) =>
            String(option.value) === String(values.vehicleCargoConfirmation)
        )
        const currentDate = format(new Date(), `DD.MM.YYYY HH:mm:ss`)
        values.internalNote = `Booker: "${
          selectedConfirmationOption?.infoText || ''
        }" at ${currentDate}`
      }

      reservationStore.setValues(values)

      const [bookingCode] = await reservationStore.makeReservation(action, values)
      const params = new URLSearchParams()

      const path = `/done/${bookingCode}`

      if (!bookingCode) {
        ErrorStore.removeLoader()
        return
      }

      // Save selected value after submit
      setVehicleCargoConfirmationValue(values.vehicleCargoConfirmation)

      if (action === EDIT) {
        if (!userStore.isFeatureEnabled) {
          fetchBooking(bookingCode)

          toast({
            title: `Booking: ${bookingCode} updated successfully`,
            status: 'success'
          })

          ErrorStore.removeLoader()

          return
        }

        const [driver] = await reservationStore.generateDriverToken({
          email,
          bookingCode,
          mobile: phone,
          expiryTime: reservationStore.selectedDeparture.departureDatetime
        })

        const sendingStatus: boolean[] = []

        if (driver && Object.values(driver).length) {
          sendingStatus.push(driver.smsSent, driver.emailSent)
        }

        fetchBooking(bookingCode)

        toast({
          title: `Booking: ${bookingCode} updated successfully`,
          description: sendingStatus.some((status) => status)
            ? 'Login sent to the driver successfully'
            : undefined,
          status: 'success'
        })
      }

      if (!email && !phone) {
        ErrorStore.removeLoader()
        navigate(path)
        return
      }

      const [driver] = await reservationStore.generateDriverToken({
        email,
        bookingCode,
        mobile: phone,
        expiryTime: reservationStore.selectedDeparture.departureDatetime
      })

      if (driver && Object.values(driver).length) {
        params.append('smsSent', JSON.stringify(driver.smsSent))
        params.append('emailSent', JSON.stringify(driver.emailSent))
      }

      ErrorStore.removeLoader()

      navigate({ pathname: `/done/${bookingCode}`, search: params.toString() })
    },
    () => {
      toast({
        status: 'error',
        description:
          'Please proof the information above to make or create the booking'
      })
      ErrorStore.removeLoader()
    }
  )

  const driverListInvalid = !!methods.formState.errors.drivers?.length
  const cargoInformationInvalid = cargoInfoFields.some(
    (field) => !!methods.formState.errors[field]
  )

  const cargoInfoErrors = getCargoInfoErrors(methods.formState.errors)

  return (
    <View title={t(action === 'edit' ? 'edit_reservation' : 'new_reservation')}>
      <FormProvider {...methods}>
        <Accordion
          allowToggle
          defaultIndex={active}
          index={active}
          border="1px solid #E2E8F0"
        >
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <AccordionButton
                  onClick={() => toggleActive(0)}
                  bgColor={(isExpanded && 'baseCyan') || undefined}
                >
                  <EventIcon fill={isDepartureValid === false ? 'red' : undefined} />
                  <Box
                    color={isDepartureValid === false ? 'red' : undefined}
                    flex="1"
                    textAlign="left"
                    ml={3}
                    fontSize="md"
                    py={2}
                  >
                    {t('departure_time_and_date')}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <DepartureComponent
                    id={0}
                    action={action}
                    active={active === 0}
                    onMakeReservation={onMakeReservation}
                    toggleActive={toggleActive}
                  />
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <AccordionButton
                  onClick={() => toggleActive(1)}
                  bgColor={(isExpanded && 'baseCyan') || undefined}
                >
                  <AssignmentIcon
                    fill={cargoInformationInvalid ? 'red' : undefined}
                  />
                  <Box
                    color={cargoInformationInvalid ? 'red' : undefined}
                    flex="1"
                    textAlign="left"
                    ml={3}
                    fontSize="md"
                    py={2}
                  >
                    {!!cargoInfoErrors.length ? (
                      <BaseFormLabel
                        tooltipProps={{ isDisabled: !cargoInfoErrors.length }}
                        tooltipText={
                          <OrderedList spacing={3}>
                            {cargoInfoErrors.map((error, index) => (
                              <ListItem key={index}>{error}</ListItem>
                            ))}
                          </OrderedList>
                        }
                        icon={ErrorIcon}
                        label={t('cargo_information')}
                      />
                    ) : (
                      t('cargo_information')
                    )}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <CargoInformation
                    id={1}
                    action={action}
                    active={active === 1}
                    onMakeReservation={onMakeReservation}
                    toggleActive={toggleActive}
                    isInvalid={cargoInformationInvalid}
                  />
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
          <AccordionItem>
            <AccordionItem>
              {({ isExpanded }) => (
                <DriverServiceAccordion
                  action={action}
                  active={active}
                  driverListInvalid={driverListInvalid}
                  onMakeReservation={onMakeReservation}
                  toggleActive={toggleActive}
                  isExpanded={isExpanded}
                />
              )}
            </AccordionItem>
          </AccordionItem>
          <AccordionItem>
            {({ isExpanded }) => (
              <>
                <AccordionButton
                  onClick={() => toggleActive(3)}
                  bgColor={(isExpanded && 'baseCyan') || undefined}
                >
                  <DoneIcon />
                  <Box flex="1" textAlign="left" ml={3} fontSize="md" py={2}>
                    {t('confirmation')}
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <Confirmation
                    id={3}
                    active={active === 3}
                    action={action}
                    onMakeReservation={onMakeReservation}
                    toggleActive={toggleActive}
                  />
                </AccordionPanel>
              </>
            )}
          </AccordionItem>
        </Accordion>
      </FormProvider>
      { (action === 'edit' && reservationStore.hasUnsavedChanges) && (
        <div
          className={css.unsavedChangesReminder}
        >
          {t('unsaved_changes_warning')}
        </div>)
      }
    </View>
  )
})

export default ReservationContainer
